import { type HighlightsController } from '@databases/highlights';

import { InputCapture } from '@canvas/input';

import { createDeleteIntentHighlightEffect } from './effects/highlights/create-delete-intent-highlight-effect';
import { createFillPreviewEffect } from './effects/highlights/create-fill-preview-effect';
import { createFocusBoxEffect } from './effects/highlights/create-focus-box-effect';
import { createHighlightsEffect } from './effects/highlights/create-highlights-effect';
import { EffectManager } from './effects/manager/effect-manager';
import { type SelectionEffectFactory } from './effects/types';
import { createContextMenuInput } from './inputs/context-menu-input/create-context-menu-input';
import { createSharedEventLoop } from './inputs/event-loop';
import { createKeyboardInput } from './inputs/keyboard-input/create-keyboard-input';
import { InputManager } from './inputs/manager/input-manager';
import { createMouseInput } from './inputs/mouse-input/create-mouse-input';
import { type SelectionInputFactory } from './inputs/types';
import { SelectionsMachine } from './machine/selection-machine';

interface InitializeSelectionsMachineParams {
  highlightsController: HighlightsController;
}

/**
 * Initializes the selections machine for all selections state, effects, etc.
 * in Databases.
 *
 * Usage:
 * ```
 * const { machine, setup } = initializeSelectionsMachine()
 *
 * const destroy = setup();
 *
 * ...
 *
 * // on cleanup
 * destroy();
 * ```
 */
export function initializeSelectionsMachine(params: InitializeSelectionsMachineParams) {
  const machine = new SelectionsMachine();

  function setup(
    inputs: SelectionInputFactory[] = [],
    effects: SelectionEffectFactory[] = []
  ) {
    const inputCapture = InputCapture.from(document.body);
    const sharedLoop = createSharedEventLoop(inputCapture);

    const inputManager = new InputManager(machine, [
      (m) => createMouseInput(m, inputCapture, sharedLoop),
      (m) => createKeyboardInput(m, sharedLoop),
      (m) => createContextMenuInput(m),
      ...(inputs || []),
    ]);
    const effectManager = new EffectManager(machine, [
      () => createHighlightsEffect(params.highlightsController),
      () => createFocusBoxEffect(params.highlightsController),
      () => createFillPreviewEffect(params.highlightsController),
      createDeleteIntentHighlightEffect,
      ...(effects || []),
    ]);

    sharedLoop.start();
    inputManager.setup();
    effectManager.setup();

    return function destroy() {
      effectManager.dispose();
      inputManager.dispose();
      inputCapture.dispose();
      sharedLoop.stop();
    };
  }

  return {
    machine,
    setup,
  };
}
